import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createProject, getActiveProject, getProjectsList, setActiveProject } from '../api/api';
import { useAuth } from './AuthContext';

const SELECTED_PROJECT_KEY = 'selectedProject';

const ActiveProjectContext = createContext(null);

const normalizeProjects = (payload) => {
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.projects)) return payload.data.projects;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const ActiveProjectProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProjectState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [recentProjectId, setRecentProjectId] = useState('');

  const persistProject = (project) => {
    if (!project) {
      localStorage.removeItem(SELECTED_PROJECT_KEY);
      return;
    }
    localStorage.setItem(SELECTED_PROJECT_KEY, JSON.stringify(project));
  };

  const refreshProjects = useCallback(async () => {
    const res = await getProjectsList();
    const list = normalizeProjects(res);

    const normalizedRecent = String(recentProjectId || '');
    if (!normalizedRecent) {
      setProjects(list);
      return list;
    }

    const sorted = [...list].sort((a, b) => {
      const aIsRecent = String(a?.id || '') === normalizedRecent;
      const bIsRecent = String(b?.id || '') === normalizedRecent;
      if (aIsRecent && !bIsRecent) return -1;
      if (!aIsRecent && bIsRecent) return 1;
      return 0;
    });

    setProjects(sorted);
    return sorted;
  }, [recentProjectId]);

  const syncSelectedFromServer = useCallback(async (projectList = []) => {
    const list = Array.isArray(projectList) ? projectList : [];
    let active = null;

    try {
      const activeRes = await getActiveProject();
      active = activeRes?.data?.data || null;
    } catch {
      active = null;
    }

    if (active?.id) {
      const full = list.find((p) => String(p.id) === String(active.id)) || active;
      setSelectedProjectState(full);
      persistProject(full);
      return full;
    }

    const localRaw = localStorage.getItem(SELECTED_PROJECT_KEY);
    if (localRaw) {
      try {
        const local = JSON.parse(localRaw);
        if (local?.id) {
          const full = list.find((p) => String(p.id) === String(local.id));
          if (full) {
            await setActiveProject(full.id);
            setSelectedProjectState(full);
            persistProject(full);
            return full;
          }
        }
      } catch {
        localStorage.removeItem(SELECTED_PROJECT_KEY);
      }
    }

    setSelectedProjectState(null);
    persistProject(null);
    return null;
  }, []);

  useEffect(() => {
    let active = true;
    const init = async () => {
      if (!isAuthenticated) {
        if (active) {
          setProjects([]);
          setSelectedProjectState(null);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const list = await refreshProjects();
        await syncSelectedFromServer(list);
      } catch (err) {
        console.error('Failed to initialize active project', err);
      } finally {
        if (active) setLoading(false);
      }
    };

    init();
    return () => {
      active = false;
    };
  }, [isAuthenticated, refreshProjects, syncSelectedFromServer]);

  const selectProject = useCallback(async (project) => {
    if (!project?.id) return null;
    await setActiveProject(project.id);
    setSelectedProjectState(project);
    persistProject(project);
    return project;
  }, []);

  const createAndSelectProject = useCallback(async (input) => {
    const payload = typeof input === 'string' ? { name: input } : (input || {});
    const projectName = String(payload.name || '').trim();
    if (!projectName) return null;

    const res = await createProject({
      name: projectName,
      client_company: String(payload.client_company || '').trim(),
      description: String(payload.description || '').trim(),
      status: String(payload.status || 'Active').trim(),
      priority: String(payload.priority || 'Medium').trim(),
      budget: payload.budget === null || payload.budget === undefined || payload.budget === '' ? null : Number(payload.budget),
      currency: String(payload.currency || 'PKR').trim(),
      start_date: payload.start_date || null,
      end_date: payload.end_date || null,
      color: String(payload.color || '#c44569').trim() || '#c44569',
    });
    const created = res?.data?.data || null;
    if (!created) return null;

    setRecentProjectId(String(created.id || ''));

    setProjects((prev) => {
      const deduped = prev.filter((item) => String(item?.id || '') !== String(created.id || ''));
      return [created, ...deduped];
    });

    await selectProject(created);
    return created;
  }, [selectProject]);

  const openProjectPicker = useCallback(() => setPickerOpen(true), []);
  const closeProjectPicker = useCallback(() => setPickerOpen(false), []);

  const value = useMemo(() => ({
    projects,
    selectedProject,
    selectedProjectId: selectedProject?.id ? String(selectedProject.id) : '',
    loading,
    pickerOpen,
    refreshProjects,
    selectProject,
    createAndSelectProject,
    openProjectPicker,
    closeProjectPicker,
  }), [projects, selectedProject, loading, pickerOpen, refreshProjects, selectProject, createAndSelectProject, openProjectPicker, closeProjectPicker]);

  return (
    <ActiveProjectContext.Provider value={value}>
      {children}
    </ActiveProjectContext.Provider>
  );
};

export const useActiveProject = () => useContext(ActiveProjectContext);
